import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import pb from '../services/pocketbase';

const CartContext = createContext();

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [guestCartItems, setGuestCartItems] = useState([]);
  const [isCartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Charger le panier invité depuis localStorage au démarrage
  useEffect(() => {
    const savedGuestCart = localStorage.getItem('guestCart');
    if (savedGuestCart) {
      try {
        setGuestCartItems(JSON.parse(savedGuestCart));
      } catch (error) {
        console.error('Erreur lors du chargement du panier invité:', error);
      }
    }
  }, []);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCartItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const records = await pb.collection('cart_items').getFullList({
        filter: `user = "${user.id}"`,
        expand: 'product',
      });
      
      const userCart = records.map(record => ({
        ...(record.expand.product || {}),
        id: record.expand.product?.id,
        quantity: record.quantity,
        cartItemId: record.id,
      }));
      setCartItems(userCart.filter(item => item.id)); // S'assurer que les produits existent
    } catch (error) {
      console.error("Erreur lors de la récupération du panier:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (product) => {
    if (!product || !product.id) return;

    if (user) {
      // Utilisateur connecté - utiliser la base de données
      const existingItem = cartItems.find(item => item.id === product.id);
      if (existingItem) {
        await updateQuantity(product.id, 1);
        return;
      }

      try {
        const newRecord = await pb.collection('cart_items').create({
          user: user.id,
          product: product.id,
          quantity: 1,
        });
        const newItem = { 
          ...product, 
          quantity: 1, 
          cartItemId: newRecord.id 
        };
        setCartItems(prevItems => [...prevItems, newItem]);
      } catch (error) {
        console.error("Erreur lors de l'ajout au panier DB:", error);
      }
    } else {
      // Utilisateur invité - utiliser localStorage
      const existingItem = guestCartItems.find(item => item.id === product.id);
      if (existingItem) {
        const updatedItems = guestCartItems.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
        setGuestCartItems(updatedItems);
        localStorage.setItem('guestCart', JSON.stringify(updatedItems));
      } else {
        const newItem = { ...product, quantity: 1 };
        const updatedItems = [...guestCartItems, newItem];
        setGuestCartItems(updatedItems);
        localStorage.setItem('guestCart', JSON.stringify(updatedItems));
      }
    }
  };

  const removeFromCart = async (productId) => {
    if (user) {
      // Utilisateur connecté
      const itemToRemove = cartItems.find(item => item.id === productId);
      if (!itemToRemove) return;

      // Optimistic update
      setCartItems((prevItems) => prevItems.filter(item => item.id !== productId));

      if (itemToRemove.cartItemId) {
        try {
          await pb.collection('cart_items').delete(itemToRemove.cartItemId);
        } catch (error) {
          console.error("Erreur lors de la suppression de l'article DB:", error);
          // Rollback
          setCartItems(cartItems);
        }
      }
    } else {
      // Utilisateur invité
      const updatedItems = guestCartItems.filter(item => item.id !== productId);
      setGuestCartItems(updatedItems);
      localStorage.setItem('guestCart', JSON.stringify(updatedItems));
    }
  };
  
  const updateQuantity = async (productId, amount) => {
    if (user) {
      // Utilisateur connecté
      const itemToUpdate = cartItems.find(item => item.id === productId);
      if (!itemToUpdate) return;

      const newQuantity = itemToUpdate.quantity + amount;

      if (itemToUpdate.cartItemId) {
        try {
          if (newQuantity > 0) {
            await pb.collection('cart_items').update(itemToUpdate.cartItemId, { quantity: newQuantity });
            setCartItems(prevItems => 
              prevItems.map(item => 
                item.id === productId ? { ...item, quantity: newQuantity } : item
              )
            );
          } else {
            await removeFromCart(productId);
          }
        } catch (error) {
          console.error("Erreur lors de la mise à jour de la quantité DB:", error);
        }
      }
    } else {
      // Utilisateur invité
      const itemToUpdate = guestCartItems.find(item => item.id === productId);
      if (!itemToUpdate) return;

      const newQuantity = itemToUpdate.quantity + amount;

      if (newQuantity > 0) {
        const updatedItems = guestCartItems.map(item => 
          item.id === productId ? { ...item, quantity: newQuantity } : item
        );
        setGuestCartItems(updatedItems);
        localStorage.setItem('guestCart', JSON.stringify(updatedItems));
      } else {
        await removeFromCart(productId);
      }
    }
  };

  const toggleCart = () => {
    setCartOpen(!isCartOpen);
  };

  // Obtenir les articles du panier actuel (connecté ou invité)
  const currentCartItems = useMemo(() => {
    return user ? cartItems : guestCartItems;
  }, [user, cartItems, guestCartItems]);

  const cartTotal = useMemo(() => {
    return currentCartItems.reduce((total, item) => total + (item.price || 0) * item.quantity, 0);
  }, [currentCartItems]);

  const value = {
    cartItems: currentCartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    isCartOpen,
    toggleCart,
    cartTotal,
    loading,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}